import React, { useMemo, useState } from "react";
import { ExternalLink, Loader2, ShoppingBag, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCart } from "../hooks/use-cart";
import { formatCredit } from "@/features/orders/utils/format-price";
import { useCartStore } from "../store/cart-store";
import { CartItemCard } from "../components/CartItemCard";
import Button from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { fashionPaths } from "@/modules";

export const CartSection: React.FC = () => {
  const { t } = useTranslation();
  const {
    isEmpty,
    items: flatItems,
    subtotal,
    cashback,
    formatPrice,
  } = useCart();
  const { merchants, updateSelection } = useCartStore();

  // Convert flat items array to merchant groups with CartItem objects
  const merchantGroupsWithItems = useMemo(() => {
    return merchants.map((merchant) => {
      // Her merchant için items'ı flat items'tan bul
      const merchantItems = flatItems.filter(
        (item) => item.vendor?.id === merchant.merchantId,
      );

      return {
        ...merchant,
        cartItems: merchantItems, // CartItem[] type
      };
    });
  }, [merchants, flatItems]);

  // Loading state while a selection change is being persisted + refetched
  const [isUpdatingSelection, setIsUpdatingSelection] = useState(false);

  const handleSelectionChange = async (
    cartItemIds: string[],
    isSelected: boolean,
  ) => {
    setIsUpdatingSelection(true);
    try {
      await updateSelection(cartItemIds, isSelected);
    } finally {
      setIsUpdatingSelection(false);
    }
  };

  // Boş sepet durumu
  if (isEmpty) {
    return (
      <div className="flex flex-col gap-4 md:gap-6">
        <div className="flex items-center gap-2">
          <h1 className="text-xl md:text-2xl font-semibold text-gray-900 dark:text-gray-100">
            {t("cart.cart")}
          </h1>
        </div>
        <div className="flex justify-center">
          <div className="p-6 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
              <ShoppingBag className="h-8 w-8 text-muted-foreground" />
            </div>
            <h4 className="font-medium mb-2">{t("cart.emptyCart")}</h4>
            <p className="text-sm text-muted-foreground mb-4">
              {t("cart.emptyCartDescription")}
            </p>
            <Button asChild variant="darkPrimary">
              <Link to={fashionPaths.shop()}>
                <ShoppingBag className="h-4 w-4 mr-2" />
                {t("cart.startShopping")}
              </Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 md:gap-6 ">
      {/* Cart Content - Split Layout: per-seller cards + checkout summary */}
      <div className="flex flex-col md:flex-row gap-4 md:gap-6">
        {/* Left Column: Seller Cards */}
        <div className="flex-1 relative flex flex-col gap-4 md:gap-6">
          {/* Loading overlay while selection is persisted + refetched */}
          {isUpdatingSelection && (
            <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/60 dark:bg-gray-900/60 backdrop-blur-[1px]">
              <Loader2 className="h-6 w-6 animate-spin text-gray-500 dark:text-gray-400" />
            </div>
          )}
          {merchantGroupsWithItems.map((merchantGroup) => {
            // Get merchant info from first item
            const firstItem = merchantGroup.items[0];
            const merchantLogo = firstItem?.offer?.sellerLogoUrl;

            // Extract base URL from product URL (e.g., https://www.pazarama.com)
            const getMerchantBaseUrl = (productUrl?: string) => {
              if (!productUrl) return undefined;
              try {
                const url = new URL(productUrl);
                return url.origin; // Returns: https://www.pazarama.com
              } catch {
                return undefined;
              }
            };

            const merchantUrl = getMerchantBaseUrl(firstItem?.offer?.url);

            // Merchant-level selection: header is checked only when ALL of its
            // items are selected (selection state comes from backend isSelected)
            const merchantItemIds = merchantGroup.cartItems.map(
              (item) => item._id,
            );
            const allSelected =
              merchantItemIds.length > 0 &&
              merchantGroup.cartItems.every((item) => item.isSelected);

            return (
              <div
                key={merchantGroup.merchantId}
                className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
              >
                {/* Seller Header */}
                <div className="flex items-center gap-2 px-4 md:px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/40">
                  <Checkbox
                    aria-label={merchantGroup.merchantName}
                    checked={allSelected}
                    disabled={isUpdatingSelection}
                    onCheckedChange={(checked) =>
                      handleSelectionChange(merchantItemIds, checked === true)
                    }
                  />

                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t("cart.seller")}:
                  </span>

                  {/* Merchant Icon/Logo */}
                  {merchantLogo && (
                    <img
                      src={merchantLogo}
                      alt={merchantGroup.merchantName}
                      className="w-4 h-4 object-contain"
                    />
                  )}

                  {/* Merchant Name (External Link) */}
                  <a
                    href={merchantUrl || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      if (!merchantUrl) {
                        e.preventDefault();
                      }
                    }}
                    className="inline-flex items-center gap-1.5 hover:underline"
                  >
                    <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {merchantGroup.merchantName}
                    </span>
                    <ExternalLink className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400" />
                  </a>
                </div>

                {/* Merchant Items - Always Visible */}
                <div className="flex flex-col p-4 md:p-6">
                  {merchantGroup.cartItems.map((item, itemIndex) => (
                    <div key={item._id}>
                      <div className="flex items-center gap-3 md:gap-4">
                        <Checkbox
                          aria-label={item.title}
                          checked={!!item.isSelected}
                          disabled={isUpdatingSelection}
                          onCheckedChange={(checked) =>
                            handleSelectionChange([item._id], checked === true)
                          }
                        />
                        <CartItemCard
                          item={item}
                          variant="cart-page"
                          showBorder={false}
                          showShadow={false}
                          imageSize="xl"
                          className="flex-1"
                        />
                      </div>
                      {/* Divider between items */}
                      {itemIndex < merchantGroup.cartItems.length - 1 && (
                        <div className="h-px bg-gray-200 dark:bg-gray-700 my-2 md:my-3" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Right Column: Checkout Summary */}
        <div className="w-full md:w-[400px] h-fit border border-gray-200 dark:border-gray-700 rounded-lg p-4 md:p-6 flex flex-col gap-4">
          {/* Subtotal */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t("cart.subtotal")}
            </span>
            <span className="text-lg md:text-xl font-semibold text-gray-900 dark:text-gray-100">
              {formatPrice(subtotal)}
            </span>
          </div>

          {/* Cashback */}
          {cashback > 0 && (
            <div className="flex items-center justify-between gap-2 bg-[#f0fdf4] dark:bg-green-950/30 rounded-lg px-3 py-2.5">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#17b26a] flex-shrink-0" />
                <span className="text-xs font-medium text-[#17b26a]">
                  {t("cart.cashbackEarn")}
                </span>
              </div>
              <span className="text-xs font-bold text-[#17b26a]">
                +{formatCredit(cashback)}
              </span>
            </div>
          )}

          {/* Checkout Button */}
          <Button asChild variant="darkPrimary" className="w-full">
            <Link to="/checkout">{t("cart.continueToCheckout")}</Link>
          </Button>

          {/* Tax Note */}
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
            {t("cart.taxesAndShippingNote")}
          </p>
        </div>
      </div>
    </div>
  );
};
